import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Bookmark, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { UserShortcut } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { SECTION_TITLE, useCurrentShortcutPath, useShortcutActions, useShortcutOpens } from './shortcuts';
import { ShortcutNameDialog } from './ShortcutDialogs';

export const UNAVAILABLE_TITLE = 'You can no longer open this page';

type RowCls = (p: { isActive: boolean }) => string;

/**
 * t607u: the rows under "My Shortcuts" (Home area of the desktop sidebar). Each opens its page; the ⋯ button (or a
 * right-click) has Rename, Move up, Move down and Remove. A page you can no longer open is greyed out and can only be
 * removed. The Sidebar hides the whole section while the list is empty.
 */
export function ShortcutRows({ list, itemCls }: { list: UserShortcut[]; itemCls: RowCls }) {
  const current = useCurrentShortcutPath();
  const opens = useShortcutOpens();
  const actions = useShortcutActions();
  const toast = useUi(s => s.toast);
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [renaming, setRenaming] = useState<UserShortcut | null>(null);
  const safe = (fn: () => Promise<unknown>, done?: string) => void fn().then(() => { if (done) toast(done, 'success'); }, e => toast(errorMessage(e), 'error'));
  const target = menu ? list.find(s => s.id === menu.id) : undefined;
  const at = target ? list.indexOf(target) : -1;
  return (
    <>
      {list.map(s => {
        const ok = opens(s.path);
        const openMenu = (x: number, y: number) => setMenu(m => (m?.id === s.id ? null : { id: s.id, x, y }));
        const inner = <><Bookmark className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden /><span className="truncate">{s.label}</span></>;
        return (
          <div key={s.id} className="group/sc relative" data-testid="sidebar-shortcut" data-path={s.path} data-unavailable={ok ? undefined : ''}
            onContextMenu={e => { e.preventDefault(); openMenu(e.clientX, e.clientY); }}>
            {ok ? (
              <Link to={s.path} className={clsx(itemCls({ isActive: current === s.path }), 'pr-8')} style={{ paddingLeft: 24 }} title={s.label}
                aria-current={current === s.path ? 'page' : undefined}>{inner}</Link>
            ) : (
              <span aria-disabled="true" title={UNAVAILABLE_TITLE} className={clsx(itemCls({ isActive: false }), 'cursor-not-allowed pr-8 opacity-50 hover:bg-transparent hover:text-sidebar-fg')} style={{ paddingLeft: 24 }}>{inner}</span>
            )}
            <button type="button" aria-label={`Options for ${s.label}`} title="Options" aria-haspopup="menu" aria-expanded={menu?.id === s.id} data-testid="sidebar-shortcut-options"
              onClick={e => { const r = e.currentTarget.getBoundingClientRect(); openMenu(r.left, r.bottom + 4); }}
              className={clsx('absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-sidebar-muted transition hover:bg-sidebar-hover hover:text-white focus-visible:opacity-100 group-hover/sc:opacity-100', menu?.id === s.id ? 'opacity-100' : 'opacity-0')}>
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
      {menu && target && (
        <RowMenu x={menu.x} y={menu.y} label={target.label} onClose={() => setMenu(null)}
          onRename={() => setRenaming(target)}
          onUp={at > 0 ? () => safe(() => actions.move(list, target.id, -1)) : undefined}
          onDown={at < list.length - 1 ? () => safe(() => actions.move(list, target.id, 1)) : undefined}
          onRemove={() => safe(() => actions.remove(target.id), `Removed from ${SECTION_TITLE}`)} />
      )}
      {renaming && <ShortcutNameDialog title="Rename shortcut" initial={renaming.label} onClose={() => setRenaming(null)} onSave={label => actions.rename(renaming.id, label)} />}
    </>
  );
}

const itemCss = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:text-slate-200 dark:hover:bg-slate-700 dark:focus:bg-slate-700';

/** In a portal so it sits over the page even from the sliding (transformed) sidebar overlay. Esc or a click outside closes it. */
function RowMenu({ x, y, label, onClose, onRename, onUp, onDown, onRemove }: {
  x: number; y: number; label: string; onClose: () => void; onRename: () => void; onUp?: () => void; onDown?: () => void; onRemove: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)), top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)) });
  }, [x, y]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node) && !(e.target as Element)?.closest?.('[data-testid="sidebar-shortcut-options"]')) closeRef.current(); };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => window.addEventListener('mousedown', onDown), 0);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onDown); };
  }, []);
  const run = (fn: () => void) => () => { onClose(); fn(); };
  return createPortal(
    <div ref={ref} role="menu" aria-label={`${label} options`} data-testid="sidebar-shortcut-menu" style={{ left: pos.left, top: pos.top }}
      className="on-page fixed z-50 w-48 overflow-hidden rounded-lg bg-white py-1 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
      <button type="button" role="menuitem" className={itemCss} data-testid="shortcut-rename" onClick={run(onRename)}><Pencil className="h-4 w-4" />Rename</button>
      {onUp && <button type="button" role="menuitem" className={itemCss} data-testid="shortcut-move-up" onClick={run(onUp)}><ArrowUp className="h-4 w-4" />Move up</button>}
      {onDown && <button type="button" role="menuitem" className={itemCss} data-testid="shortcut-move-down" onClick={run(onDown)}><ArrowDown className="h-4 w-4" />Move down</button>}
      <button type="button" role="menuitem" className={clsx(itemCss, 'text-rose-600 dark:text-rose-400')} data-testid="shortcut-remove" onClick={run(onRemove)}><Trash2 className="h-4 w-4" />Remove</button>
    </div>,
    document.body,
  );
}
